import { Injectable } from '@angular/core';
import {
  uploadBytesResumable as firebaseUploadBytesResumable,
  UploadMetadata,
  UploadTask,
  StorageReference,
} from '@angular/fire/storage';
import { DeviceAndBrowserDetectorService } from './device-and-browser-detector.service';

/**
 * Rebuilds a Blob/File from an ArrayBuffer so Firebase's resumable uploader
 * sees consistent size and bytes. Fixes iPadOS/Safari
 * "Client uploaded to the wrong offset (262144 instead of 0)".
 */
async function materializeBlobForUpload(data: Blob): Promise<Blob> {
  const buffer = await data.arrayBuffer();
  if (data instanceof File) {
    return new File([buffer], data.name, {
      type: data.type || 'application/octet-stream',
      lastModified: data.lastModified,
    });
  }
  return new Blob([buffer], { type: data.type });
}

/**
 * Sync stand-in for UploadTask while rematerialize runs (iOS only).
 * Progress starts once the real Firebase task is bound.
 */
function createDeferredUploadTask(): {
  proxy: UploadTask;
  bind: (task: UploadTask) => void;
  fail: (error: unknown) => void;
} {
  let real: UploadTask | undefined;
  let cancelled = false;
  const pendingOn: Array<Parameters<UploadTask['on']>> = [];

  let resolve!: (value: unknown) => void;
  let reject!: (reason?: unknown) => void;
  const done = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });

  const proxy = {
    cancel: () => {
      cancelled = true;
      return real?.cancel() ?? true;
    },
    on: ((...args: Parameters<UploadTask['on']>) => {
      if (real) {
        return real.on(...args);
      }
      pendingOn.push(args);
      return () => undefined;
    }) as UploadTask['on'],
    then: ((onFulfilled?: any, onRejected?: any) =>
      done.then(onFulfilled, onRejected)) as UploadTask['then'],
    catch: ((onRejected?: any) => done.catch(onRejected)) as UploadTask['catch'],
    pause: () => real?.pause() ?? true,
    resume: () => real?.resume() ?? true,
    get snapshot() {
      return real?.snapshot;
    },
  } as UploadTask;

  return {
    proxy,
    bind(task) {
      real = task;
      if (cancelled) {
        task.cancel();
      }
      for (const args of pendingOn) {
        task.on(...args);
      }
      task.then(resolve, reject);
    },
    fail: reject,
  };
}

@Injectable({
  providedIn: 'root',
})
export class FirebaseStorageUploadService {
  constructor(private deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService) {}

  /**
   * Drop-in for Firebase `uploadBytesResumable`.
   * On iPhone/iPad (`isiOS()`): rematerialize Blob/File first, then upload.
   * Elsewhere: call Firebase directly.
   */
  uploadBytesResumable(
    ref: StorageReference,
    data: Blob | Uint8Array | ArrayBuffer,
    metadata?: UploadMetadata,
  ): UploadTask {
    if (!(data instanceof Blob) || !this.deviceAndBrowserDetectorService.isiOS()) {
      return firebaseUploadBytesResumable(ref, data, metadata);
    }

    const deferred = createDeferredUploadTask();
    void materializeBlobForUpload(data)
      .then((materialized) => {
        deferred.bind(firebaseUploadBytesResumable(ref, materialized, metadata));
      })
      .catch(deferred.fail);

    return deferred.proxy;
  }
}
